import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Zap } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TierCard = ({ tier, onBuy, loading }) => (
  <div
    data-testid={`tier-card-${tier.tier_id}`}
    className="tier-card relative bg-card border border-border rounded-xl p-6 hover:shadow-lg hover:border-accent/50"
  >
    {tier.badge && (
      <Badge className="absolute -top-2.5 right-4 bg-accent text-accent-foreground hover:bg-accent">{tier.badge}</Badge>
    )}
    <div className="flex items-baseline justify-between mb-4">
      <h3 className="font-heading font-medium text-lg tracking-tight">{tier.name}</h3>
      <div className="text-right">
        <span className="font-heading text-2xl font-semibold">${tier.price.toFixed(2)}</span>
        <span className="text-xs text-muted-foreground ml-1">{tier.billing === "monthly" ? "/mo" : "once"}</span>
      </div>
    </div>
    <ul className="space-y-2 mb-5">
      {tier.features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
          <Check className="w-4 h-4 text-accent mt-0.5 shrink-0" />
          {f}
        </li>
      ))}
    </ul>
    <Button
      data-testid={`checkout-btn-${tier.tier_id}`}
      onClick={() => onBuy(tier)}
      disabled={loading === tier.tier_id}
      className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-medium transition-transform duration-300 hover:-translate-y-0.5"
    >
      {loading === tier.tier_id ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <>
          <Zap className="w-4 h-4 mr-2" />
          {tier.billing === "monthly" ? "Subscribe" : "Unlock Now"}
        </>
      )}
    </Button>
  </div>
);

export const CheckoutPanel = ({ tiers }) => {
  const [loading, setLoading] = useState(null);
  const oneTime = tiers.filter((t) => t.billing === "one_time");
  const monthly = tiers.filter((t) => t.billing === "monthly");

  const buy = async (tier) => {
    setLoading(tier.tier_id);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        tier_id: tier.tier_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <div data-testid="checkout-panel" className="bg-muted/40 border border-border/60 rounded-2xl p-6">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Upgrade &amp; Unlock</p>
      <Tabs defaultValue="one_time">
        <TabsList className="w-full h-auto grid grid-cols-2 mb-5 rounded-full p-1">
          <TabsTrigger data-testid="tab-one-time" value="one_time" className="rounded-full py-2">One-Time</TabsTrigger>
          <TabsTrigger data-testid="tab-subscriptions" value="monthly" className="rounded-full py-2">Subscriptions</TabsTrigger>
        </TabsList>
        <TabsContent value="one_time" className="space-y-4 mt-0">
          {oneTime.map((t) => <TierCard key={t.tier_id} tier={t} onBuy={buy} loading={loading} />)}
          {oneTime.length === 0 && <p className="text-sm text-muted-foreground">No one-time upgrades available.</p>}
        </TabsContent>
        <TabsContent value="monthly" className="space-y-4 mt-0">
          {monthly.map((t) => <TierCard key={t.tier_id} tier={t} onBuy={buy} loading={loading} />)}
          {monthly.length === 0 && <p className="text-sm text-muted-foreground">No subscriptions available.</p>}
        </TabsContent>
      </Tabs>
      <p className="text-[11px] text-muted-foreground mt-4 text-center">
        Secure checkout powered by Stripe. You'll be redirected to complete payment.
      </p>
    </div>
  );
};
